import { useCallback, useMemo } from 'react';

import type { Annotation } from '../features/annotation';
import { inspectionApi } from '../features/inspections/inspectionAPI';
import { convertAnnotationToOBBYolov8, convertAnnotationsToImageCoordinates } from '../features/annotation';
import type { AnnotationDropdownOption } from '../features/annotation/components/AnnotationFilterDropdown';
import type { InspectionAsset } from '../types/common/asset';
import type { InspectionDamage } from '../features/inspections/inspectionTypes';
import { normalizeInspectionViewName } from '../features/inspections/inspectionViewConstants';

type MediaImageItemData = {
  id?: string;
  imageFileId?: string;
  imageURI: string;
  title: string;
  videoFrameTime?: number;
  raw?: InspectionAsset | Record<string, unknown>;
};

type CreatedDamageResponse = Partial<InspectionDamage> & {
  clientRef?: string;
  imageFileId?: string;
};

type CreateInspectionDamageResponse = {
  damage?: CreatedDamageResponse[];
  damages?: CreatedDamageResponse[];
  [key: string]: unknown;
};

interface UseMediaImageAnnotationsParams {
  inspectionId: string;
  item: MediaImageItemData;
  viewName: string;
  annotations: Annotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  damageAreaOptions?: AnnotationDropdownOption[];
  damageTypeOptions?: AnnotationDropdownOption[];
  damageSeverityOptions?: AnnotationDropdownOption[];
  onAnnotationChanged?: () => Promise<void> | void;
  onImageResolved?: (info: { imageFileId: string; imageURI?: string }) => void;
}

const createCodeToIdMap = (options?: AnnotationDropdownOption[]) =>
  new Map((options ?? []).map(option => [option.code, option.id ?? null]));

const isNewAnnotation = (annotation: Annotation) => annotation.id.includes('new_');

const getDamageCode = (annotation: Annotation) => String(annotation.type ?? '').substring(0, 5);

const hasCompleteDamageSelection = (annotation: Annotation) => {
  const damageCode = getDamageCode(annotation);

  return (
    damageCode.length === 5 &&
    damageCode.substring(0, 2) !== '00' &&
    damageCode.substring(2, 4) !== '00' &&
    damageCode.substring(4, 5) !== '0'
  );
};

const isCreatableAnnotation = (annotation: Annotation) =>
  isNewAnnotation(annotation) && annotation.deleted !== 1 && hasCompleteDamageSelection(annotation);

const hasIncompleteNewAnnotation = (annotation: Annotation) =>
  isNewAnnotation(annotation) && annotation.deleted !== 1 && !hasCompleteDamageSelection(annotation);

const getCreatedDamages = (response: CreateInspectionDamageResponse): CreatedDamageResponse[] => {
  const created = response?.damage ?? response?.damages ?? [];
  return Array.isArray(created) ? created : [];
};

export const useMediaImageAnnotations = ({
  inspectionId,
  item,
  viewName,
  annotations,
  setAnnotations,
  damageAreaOptions,
  damageTypeOptions,
  damageSeverityOptions,
  onAnnotationChanged,
  onImageResolved,
}: UseMediaImageAnnotationsParams) => {
  const [createInspectionDamage] = inspectionApi.useCreateInspectionDamageMutation();
  const [updateInspectionDamage] = inspectionApi.useUpdateInspectionDamageMutation();
  const [deleteInspectionDamage] = inspectionApi.useDeleteInspectionDamageMutation();
  const [approveInspectionDamage] = inspectionApi.useApproveInspectionDamageMutation();

  const damageAreaIds = useMemo(() => createCodeToIdMap(damageAreaOptions), [damageAreaOptions]);
  const damageTypeIds = useMemo(() => createCodeToIdMap(damageTypeOptions), [damageTypeOptions]);
  const damageSeverityIds = useMemo(() => createCodeToIdMap(damageSeverityOptions), [damageSeverityOptions]);

  const buildDamagePayload = useCallback(
    (annotation: Annotation) => {
      const annotationMetadata = convertAnnotationsToImageCoordinates(annotation);
      const obb = convertAnnotationToOBBYolov8(annotation, annotation.imageWidth ?? 3840, annotation.imageHeight ?? 2160, {
        items: damageTypeOptions ?? [],
      });

      return {
        // Echoed back by the server on create so the bulk-create response can
        // be matched back to the local annotation by identity rather than by
        // array position (the server isn't guaranteed to preserve request order).
        clientRef: annotation.id,
        yoloString: obb,
        newMetadata: {
          ...annotationMetadata,
        },
        aiagDamageAreaId: damageAreaIds.get(annotation.type?.substring(0, 2)) ?? null,
        aiagDamageTypeId: damageTypeIds.get(annotation.type?.substring(2, 4)) ?? null,
        aiagDamageSeverityId: damageSeverityIds.get(annotation.type?.substring(4, 5)) ?? null,
      };
    },
    [damageAreaIds, damageSeverityIds, damageTypeIds, damageTypeOptions]
  );

  const buildCreatePayload = useCallback(
    (nextAnnotations: Annotation[]) => ({
      videoZoomScale: 0,
      // Real captured frame time (falls back to 0 for non-video sources). Stored
      // on the damage so the still-frame endpoint can dedup near-duplicate frames.
      videoFrameTime: Number(item.videoFrameTime) || 0,
      ...(item.imageFileId ? { imageFileId: item.imageFileId } : {}),
      imageURI: item.imageURI,
      viewName: normalizeInspectionViewName(viewName) || viewName,
      status: 'open',
      source: 'manual',
      damages: nextAnnotations.map(buildDamagePayload),
    }),
    [buildDamagePayload, item.imageFileId, item.imageURI, item.videoFrameTime, viewName]
  );

  const saveAnnotation = useCallback(
    async (annotation: Annotation): Promise<'created' | 'updated' | 'noop'> => {
      const damageId = annotation.id;

      if (isNewAnnotation(annotation)) {
        if (annotations.some(hasIncompleteNewAnnotation)) {
          throw new Error('Please select all 3 dropdown values before saving the annotation.');
        }

        const newAnnotations = annotations.filter(isCreatableAnnotation);

        if (newAnnotations.length === 0) {
          return 'noop';
        }

        const payload = buildCreatePayload(newAnnotations);
        const response = (await createInspectionDamage({ inspectionId, payload }).unwrap()) as CreateInspectionDamageResponse;
        const createdDamages = getCreatedDamages(response);

        // A brand-new frame gets its image copied to damages/<inspId>/<newId> on
        // this first create. Surface the resolved imageFileId so the modal links
        // any further annotations on the same frame to it (no duplicate copy).
        if (!item.imageFileId) {
          const resolvedImageFileId = createdDamages.find(damage => damage?.imageFileId)?.imageFileId;
          if (resolvedImageFileId) {
            onImageResolved?.({ imageFileId: String(resolvedImageFileId) });
          }
        }

        if (createdDamages.length > 0) {
          const createdDamageIds = createdDamages
            .map(damage => (damage?.id != null ? String(damage.id) : ''))
            .filter(Boolean);

          // Manual UI creates are auto-approved so they appear as GSIP-verified.
          if (createdDamageIds.length > 0) {
            await Promise.all(
              createdDamageIds.map(createdDamageId =>
                approveInspectionDamage({ inspectionId, damageId: createdDamageId, payload: {} }).unwrap()
              )
            );
          }

          const approvedDamageIds = new Set(createdDamageIds);

          setAnnotations(previousAnnotations =>
            previousAnnotations.map(currentAnnotation => {
              if (!isNewAnnotation(currentAnnotation)) {
                return currentAnnotation;
              }

              const createdIndex = newAnnotations.findIndex(newAnnotation => newAnnotation.id === currentAnnotation.id);
              if (createdIndex < 0) {
                return currentAnnotation;
              }

              // Prefer matching by the clientRef we sent, so an out-of-order
              // response can't assign the wrong server id to a box. Fall back
              // to positional matching only if the backend doesn't echo it back.
              const createdDamage =
                createdDamages.find(damage => damage?.clientRef === currentAnnotation.id) ?? createdDamages[createdIndex];

              if (!createdDamage?.id) {
                return currentAnnotation;
              }

              const nextId = String(createdDamage.id);
              return {
                ...currentAnnotation,
                id: nextId,
                action: undefined,
                deleted: 0,
                review_status: approvedDamageIds.has(nextId) ? 'APPROVED' : currentAnnotation.review_status,
              };
            })
          );
        }

        await onAnnotationChanged?.();
        return 'created';
      } else {
        const payload = buildDamagePayload(annotation);
        await updateInspectionDamage({ inspectionId, damageId, payload }).unwrap();
        setAnnotations(previousAnnotations =>
          previousAnnotations.map(currentAnnotation =>
            currentAnnotation.id === damageId
              ? {
                  ...annotation,
                  action: undefined,
                  deleted: 0,
                }
              : currentAnnotation
          )
        );
        await onAnnotationChanged?.();
        return 'updated';
      }
    },
    [
      annotations,
      approveInspectionDamage,
      buildCreatePayload,
      buildDamagePayload,
      createInspectionDamage,
      inspectionId,
      item.imageFileId,
      onAnnotationChanged,
      onImageResolved,
      setAnnotations,
      updateInspectionDamage,
    ]
  );

  const deleteAnnotation = useCallback(
    async (annotationId: string) => {
      if (!annotationId.includes('new_')) {
        await deleteInspectionDamage({ inspectionId, damageId: annotationId, payload: {} }).unwrap();
      }

      setAnnotations(previousAnnotations =>
        previousAnnotations.map(annotation => (annotation.id === annotationId ? { ...annotation, deleted: 1 } : annotation))
      );

      await onAnnotationChanged?.();
    },
    [deleteInspectionDamage, inspectionId, onAnnotationChanged, setAnnotations]
  );

  const approveAnnotation = useCallback(
    async (annotationId: string) => {
      if (!annotationId.includes('new_')) {
        await approveInspectionDamage({ inspectionId, damageId: annotationId, payload: {} }).unwrap();
      }

      setAnnotations(previousAnnotations =>
        previousAnnotations.map(annotation => (annotation.id === annotationId ? { ...annotation, review_status: 'APPROVED' } : annotation))
      );

      await onAnnotationChanged?.();
    },
    [approveInspectionDamage, inspectionId, onAnnotationChanged, setAnnotations]
  );

  return {
    saveAnnotation,
    deleteAnnotation,
    approveAnnotation,
  };
};
